/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, map, Observable, of } from "rxjs";
import { BarChart } from "../../models/http/charts/bar-chart/bar-chart.model";
import { MapChart } from "../../models/http/charts/map-chart/map-chart.model";
import { PieChart } from "../../models/http/charts/pie-chart/pie-chart.model";
import { LineChart } from "../../models/http/charts/line-chart/line-chart.model";
import { HardwareAssetsRealtimeInformation } from "../../models/http/dashboard/hardware-assets-realtime-information.model";
import { HardwareAssetsRealtimeManufacturerTable } from "../../models/http/dashboard/hardware-assets-realtime-manufacturer-table.model";
import { TotalMonths } from "../../models/http/dashboard/hardware-assets-realtime-total-months.model";
import { ChartResponse } from "../../models/ui/charts.model";
import { HttpMethods } from "../../models/ui/http-methods.model";
import { ENDPOINT_GROUPS } from "../../utils/apis/apis.constants";
import { isNullOrUndefined } from "../../utils/utils";
import { CommunicationService } from "../communication.service";

@Injectable({
	providedIn: "root"
})
export class DashboardHardwareAssetsRealtimeHttpService {
	private _dashboardHardwareAssetsRealtimeOperations =
		ENDPOINT_GROUPS.dashboardHardwareAssetsRealtime.operations;

	constructor(private _communicationService: CommunicationService) {}

	public getInformations(
		startDate: string,
		endDate?: string
	): Observable<HardwareAssetsRealtimeInformation> {
		const params = new HttpParams({
			fromObject: {
				startDate,
				...(!isNullOrUndefined(endDate) && { endDate })
			}
		});

		return this._communicationService.callService(
			this._dashboardHardwareAssetsRealtimeOperations.getInformations.service,
			HttpMethods.GET,
			{ params }
		);
	}

	public getTotalEmissions(startDate: string, endDate?: string): Observable<TotalMonths> {
		const params = new HttpParams({
			fromObject: {
				startDate,
				...(!isNullOrUndefined(endDate) && { endDate })
			}
		});

		return this._communicationService.callService(
			this._dashboardHardwareAssetsRealtimeOperations.getTotalEmissions.service,
			HttpMethods.GET,
			{ params }
		);
	}

	public getEmissionsByModelTotal(
		startDate: string,
		endDate?: string
	): Observable<ChartResponse<BarChart>> {
		const params = new HttpParams({
			fromObject: {
				startDate,
				...(!isNullOrUndefined(endDate) && { endDate })
			}
		});

		return this._communicationService
			.callService(
				this._dashboardHardwareAssetsRealtimeOperations.getEmissionsByModelTotal.service,
				HttpMethods.GET,
				{ params }
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as BarChart, hasError: true }))
			);
	}

	public getEmissionsByModelPerDevice(
		startDate: string,
		endDate?: string
	): Observable<ChartResponse<BarChart>> {
		const params = new HttpParams({
			fromObject: {
				startDate,
				...(!isNullOrUndefined(endDate) && { endDate })
			}
		});

		return this._communicationService
			.callService(
				this._dashboardHardwareAssetsRealtimeOperations.getEmissionsByModelPerDevice
					.service,
				HttpMethods.GET,
				{ params }
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as BarChart, hasError: true }))
			);
	}

	public getEmissionsByLocation(country?: string): Observable<ChartResponse<BarChart>> {
		const params = new HttpParams({
			fromObject: {
				...(!isNullOrUndefined(country) && { country })
			}
		});

		return this._communicationService
			.callService(
				this._dashboardHardwareAssetsRealtimeOperations.getEmissionsByLocation.service,
				HttpMethods.GET,
				{ params }
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as BarChart, hasError: true }))
			);
	}

	public getManufacturersEmission(
		category?: string
	): Observable<HardwareAssetsRealtimeManufacturerTable> {
		const params = new HttpParams({
			fromObject: {
				...(!isNullOrUndefined(category) && { category })
			}
		});

		return this._communicationService.callService(
			this._dashboardHardwareAssetsRealtimeOperations.getManufacturersEmission.service,
			HttpMethods.GET,
			{ params }
		);
	}

	public getManufacturerPieChart(category?: string): Observable<ChartResponse<PieChart>> {
		const params = new HttpParams({
			fromObject: {
				...(!isNullOrUndefined(category) && { category })
			}
		});

		return this._communicationService
			.callService(
				this._dashboardHardwareAssetsRealtimeOperations.getManufacturerPieChart.service,
				HttpMethods.GET,
				{ params }
			)
			.pipe(
				map((response: PieChart) => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as PieChart, hasError: true }))
			);
	}

	public getCarbonIntensityMap(): Observable<ChartResponse<MapChart>> {
		return this._communicationService
			.callService(
				this._dashboardHardwareAssetsRealtimeOperations.getCarbonIntensityMap.service,
				HttpMethods.GET,
				{}
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as MapChart, hasError: true }))
			);
	}

	public getEnergyConsumption(): Observable<ChartResponse<LineChart>> {
		return this._communicationService
			.callService(
				this._dashboardHardwareAssetsRealtimeOperations.getEnergyConsumption.service,
				HttpMethods.GET,
				{}
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as LineChart, hasError: true }))
			);
	}

	public getEmbodiedEmissions(): Observable<ChartResponse<LineChart>> {
		return this._communicationService
			.callService(
				this._dashboardHardwareAssetsRealtimeOperations.getEmbodiedEmissions.service,
				HttpMethods.GET,
				{}
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as LineChart, hasError: true }))
			);
	}
}
